// Sugalarm — auto-reload-on-new-bundle poller (cycle 33).
//
// Polls /api/health on a fixed cadence and captures the very first
// `deploy.app_version` it sees as the baseline. When a subsequent
// poll reports a different value, a new build has shipped — fire a
// cache-busting navigation so Chromium can't hand back the cached
// `index.html` (which still points at the previous bundle's hashed
// asset URLs).
//
// Cache-bust shape: append `_v=<Date.now()>` to the query string. The
// pathname, hash, and every other existing query param survive
// untouched — most importantly the kiosk-flag query set by the
// systemd unit (`?kiosk=1`). The reload uses `location.replace` so
// the back stack stays clean.
//
// Belt + suspenders for the server-side cache-headers contract in
// `server/main.py` — `/` and SPA fall-through ship with
// `Cache-Control: no-cache, no-store, must-revalidate`, so an
// honest browser will revalidate even without the `_v=` bust. The
// bust covers a misconfigured intermediate proxy / CDN / cached
// service-worker installation that ignores the origin headers.

import { useEffect, useRef } from "react";

import { apiUrl } from "../env";

const POLL_INTERVAL_MS = 60_000;

interface HealthResponse {
  deploy?: { app_version?: string | null };
  version?: string | null;
}

function readVersion(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const d = data as HealthResponse;
  return d.deploy?.app_version ?? d.version ?? null;
}

/** Build the cache-bust target URL. Preserves pathname + hash and
 *  appends `_v=<Date.now()>` without dropping any pre-existing query
 *  params (kiosk=1 in particular). Exported so a future cycle that
 *  wires a manual "reload now" button or a remote-trigger frame can
 *  reuse the same shape. */
export function buildReloadUrl(loc: Location, now: number): string {
  const params = new URLSearchParams(loc.search);
  params.set("_v", String(now));
  const qs = params.toString();
  return `${loc.pathname}${qs ? "?" + qs : ""}${loc.hash ?? ""}`;
}

/** Cycle 453.2: routes where a new build must NOT reload the page. The
 *  password-reset pages carry a single-use token in the query string; the
 *  reload keeps every query param, so reloading there would re-send the
 *  token and could discard a half-typed password. The reload waits until
 *  the user leaves the page — the next poll tick after that fires it. The
 *  poller reads `pathname` only, never the query string. */
export const RELOAD_HELD_PATHS: readonly string[] = ["/reset-password", "/forgot-password"];

export function reloadHeld(pathname: string): boolean {
  return RELOAD_HELD_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export interface AppVersionPollerOpts {
  /** Disable to keep the poller dormant on /login / unauthenticated
   *  surfaces. The whole hook no-ops when false. */
  enabled?: boolean;
  /** Override the 60-second cadence. Cycle 33 uses the default
   *  everywhere; the knob exists for tests if a future cycle adds
   *  one. */
  intervalMs?: number;
}

export function useAppVersionPoller(opts: AppVersionPollerOpts = {}): void {
  const { enabled = true, intervalMs = POLL_INTERVAL_MS } = opts;
  // Initial app_version observed by the first successful poll.
  // Subsequent polls compare against this baseline; a mismatch
  // means a new build is live and we should reload.
  const baselineRef = useRef<string | null>(null);
  const firedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (firedRef.current) return;
      try {
        const res = await fetch(apiUrl("/api/health"), { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as unknown;
        const v = readVersion(data);
        if (!v) return;
        if (baselineRef.current == null) {
          baselineRef.current = v;
          return;
        }
        if (v === baselineRef.current) return;
        // Cycle 453.2: never on a reset page — try again next tick.
        if (reloadHeld(window.location.pathname)) return;
        // New build live — cache-bust the document URL.
        firedRef.current = true;
        const target = buildReloadUrl(window.location, Date.now());
        window.location.replace(target);
      } catch {
        // Network blip — the next poll tick retries. No surface UI.
      }
    };

    // Fire once on mount to capture the baseline, then on interval.
    void poll();
    const id = window.setInterval(poll, intervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled, intervalMs]);
}
